/**
 * PATH: packages/api/src/workforce/resolveBases.ts
 * PURPOSE: Pure URL resolution for the workforce FastAPI service + the
 *          Django backend origin, from the single CI env input.
 * WHY: apps/workforce and apps/hr each carried a private copy of this
 *      logic (C4-MR5 folds them). The CI build sets ONE env var for both
 *      backends (VITE_API_URL = https://…/workforce-api/api/v1); this
 *      module derives:
 *        workforceApiBase → https://…/workforce-api/api/v1  (FastAPI)
 *        backendOrigin    → https://…                        (Django)
 *
 * FLOW:
 *   (rawApiUrl, explicitOrigin) → resolveWorkforceBases() → { bases }
 *
 * DEPENDENCIES: none (pure string/URL logic — fully unit-testable)
 * RELATED:
 * - packages/api/src/workforce/client.ts: lazy singleton consumer
 */

export const WORKFORCE_API_PREFIX = '/workforce-api/api/v1'
const FALLBACK_ORIGIN = 'https://quotech-backend.quotech.ai'

export interface WorkforceBases {
  /** e.g. "https://quotech-backend.quotech.ai/workforce-api/api/v1" */
  workforceApiBase: string
  /** e.g. "https://quotech-backend.quotech.ai" (scheme + host, no path) */
  backendOrigin: string
}

/**
 * Pure: env inputs → both base URLs.
 *
 * @param rawApiUrl      VITE_API_URL as set by CI (may already carry the
 *                       workforce prefix, may be a bare origin, may be '')
 * @param explicitOrigin VITE_BACKEND_ORIGIN override (optional)
 */
export function resolveWorkforceBases(
  rawApiUrl: string,
  explicitOrigin: string,
): WorkforceBases {
  const rawApi = (rawApiUrl || '').trim()
  const rawOrigin = (explicitOrigin || '').trim()

  let origin = FALLBACK_ORIGIN
  if (rawOrigin) {
    origin = rawOrigin.replace(/\/+$/, '')
  } else if (rawApi) {
    try {
      origin = new URL(rawApi).origin
    } catch {
      /* keep fallback */
    }
  }

  const workforceApiBase =
    rawApi && rawApi.includes(WORKFORCE_API_PREFIX)
      ? normalise(rawApi)
      : `${origin}${WORKFORCE_API_PREFIX}`

  return { workforceApiBase, backendOrigin: origin }
}

/** Collapse doubled prefixes / duplicate slashes / trailing slashes. */
function normalise(raw: string): string {
  const input = (raw || '').trim()
  if (!input) return WORKFORCE_API_PREFIX

  if (/^https?:\/\//i.test(input)) {
    try {
      const url = new URL(input)
      url.pathname = normalisePath(url.pathname)
      return url.toString().replace(/\/+$/, '')
    } catch {
      /* fall through to path handling */
    }
  }
  return normalisePath(input)
}

function normalisePath(pathname: string): string {
  let p = pathname || '/'
  if (!p.startsWith('/')) p = `/${p}`
  p = p.replace(/\/{2,}/g, '/')
  while (p.includes(`${WORKFORCE_API_PREFIX}${WORKFORCE_API_PREFIX}`)) {
    p = p.replace(`${WORKFORCE_API_PREFIX}${WORKFORCE_API_PREFIX}`, WORKFORCE_API_PREFIX)
  }
  return p.replace(/\/+$/, '') || '/'
}
